import {
  type CSSProperties,
  forwardRef,
  lazy,
  Suspense,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDebounce } from 'use-debounce'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  PiArrowBendUpRight,
  PiArrowSquareOut,
  PiArrowUpRight,
  PiBuildings,
  PiCaretDown,
  PiCaretLeft,
  PiCaretRight,
  PiChatText,
  PiEnvelope,
  PiEnvelopeOpen,
  PiFlag,
  PiFloppyDisk,
  PiGlobe,
  PiHandCoins,
  PiLink,
  PiPaperPlaneRight,
  PiRobot,
  PiStack,
  PiStar,
  PiStorefront,
  PiTrash,
  PiTrendUp,
  PiUserMinus,
  PiWarning,
  PiX,
} from 'react-icons/pi'

import confirm from 'components/dialogConfirm'
import { cn } from 'components/UI/cn'
import { colors } from 'styles/tokens'
import { PillTabs } from 'components/FbUI/PillTabs'
import { Ds2026SearchInput } from 'components/FbUI/Ds2026SearchInput'
import {
  ds2026DropdownContentClass,
  ds2026DropdownItemClass,
} from 'components/FbUI/ds2026DropdownMenu'

import { StyledEditorRHF } from 'components/Editor/EditorRHF'
import { ReactCreatableSelectWrapper } from 'components/FormUtils/ReactCreatableSelectWrapper'
import {
  buildReplyAllCcOptions,
  CcBccToggles,
  emailRecipientStyles,
  filterSelf,
  formatEmailRecipientOption,
  looksLikeEmail,
  parseEmailOption,
  RecipientRow,
  ReplyMode,
  toWireRecipients,
} from 'components/FormUtils/EmailRecipientSelect'
import {
  InlineComposeShell,
  InlineReplyHeader,
  InlineReplyRecipients,
  SummaryRecipient,
  ThreadMessageCard,
  ThreadMessageData,
} from 'components/Email'
import { useAuth } from 'context/authentication/useAuth'
import { RecipientOption, useRecipientOptions } from 'hooks/useRecipientOptions'
import { SectionedSection } from 'components/UI/Select/SectionedMultiSelect'
import { InlineSelectFilter } from 'components/Filters/fields/InlineSelectFilter'
import { useGetCampaigns } from 'features/campaigns/campaigns_api'
import { formatRelativeTime } from 'utils/formatting'
import { toast } from 'react-toastify'
import axios from 'axios'
import {
  ForwardReplyPayload,
  getThreadContactCompany,
  useThreadContactCompany,
  getReplyThreadInboxLink,
  Reply,
  ThreadContactCompany,
  replyThreadKeys,
  ReplyFilters,
  ReplySummary,
  ReplyToReplyPayload,
  useForwardReply,
  useGetReplyThreadsInfinite,
  useGetReplyThreadSummary,
  useGetThreadReplies,
  useDeleteReply,
  useMarkReplyAutomated,
  useMarkReplyInterested,
  useMarkReplyRead,
  useReplyToReply,
  useToggleThreadFlag,
  useUnsubscribeReplyContact,
  useTakeOver,
  TakeOverPayload,
} from 'features/campaigns/replies_api'
import {
  nextClassificationState,
  pickClassificationReply,
} from 'features/campaigns/classificationTarget'
import { pickDetailReply } from 'features/campaigns/detailReplyTarget'
import { useQueryClient } from '@tanstack/react-query'
import {
  useGetEmailIntegrationOptions,
  useGetSendableEmailIntegrationOptions,
} from 'services/emailIntegrationApi'
import { MismatchEmailModal } from 'features/campaigns/MismatchEmailModal'
import { TakeOverModal } from 'features/campaigns/TakeOverModal'
import { ReplyTemplatesDrawer } from 'features/campaigns/ReplyTemplatesDrawer'
import { SaveReplyTemplateModal } from 'features/campaigns/SaveReplyTemplateModal'
import { ReplyTemplate } from 'features/campaigns/reply_templates_api'
import {
  buildContactTokens,
  fillVariables,
} from 'features/campaigns/email-variables'
import { Tooltip } from 'components/Tooltip/tooltip'
import { useCompanyV2Url } from 'utils/useCompanyUrl'
import { CompanyTypeSlug } from 'models/companies'
import { useGetCampaignSequence } from 'features/campaigns/sequences_api'
import { useGetWorkspaceSenderEmails } from 'features/campaigns/sender_emails_api'
import { useInfiniteScroll } from 'hooks/useInfiniteScroll'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { Dropdown, IDropdownItem } from 'components/UI/Dropdown/Dropdown'

/**
 * Lazy-loaded so the (large) deal form isn't in the critical bundle for the
 * campaign replies tab — it only renders when the user triggers Create Deal.
 */
const CreateDealModal = lazy(
  () => import('components/Deals/CreateDealModal/CreateDealModal')
)

/**
 * Same lazy-load story as `CreateDealModal`: the sample-request wizard pulls
 * in product/distributor APIs we don't want on the critical inbox bundle.
 */
const SampleRequestFormModal = lazy(() =>
  import('components/Samples/SampleRequestFormModal').then((m) => ({
    default: m.SampleRequestFormModal,
  }))
)

/**
 * Modal-opener input for the sample-request flow. Intentionally not tied to
 * the `Reply` shape so a future regular-inbox caller (or any other surface)
 * can construct the same context from its own data source without a refactor.
 */
type SampleRequestContext = {
  contactCompanyId: number
  contact: { firstName: string; lastName: string; email: string }
}

/** Split a single display-name string into first / last on the first space. */
function splitDisplayName(full: string | null | undefined): {
  firstName: string
  lastName: string
} {
  const trimmed = (full ?? '').trim()
  if (!trimmed) return { firstName: '', lastName: '' }
  const idx = trimmed.indexOf(' ')
  if (idx < 0) return { firstName: trimmed, lastName: '' }
  return { firstName: trimmed.slice(0, idx), lastName: trimmed.slice(idx + 1) }
}

/**
 * Surface a failure resolving a reply to its CRM contact-company — shared by
 * the Create-Deal and Request-Sample openers (FIR-7420). A 404 means the
 * reply's sender has no CRM contact (the resolver falls back to email-match
 * before raising, so BisonLead drift is no longer a 404 cause); offer an
 * "import" affordance. Any other error shows the caller's fallback message.
 *
 * @param e - Error thrown by `getThreadContactCompany`.
 * @param fallbackMessage - Toast shown for non-404 failures.
 * @param navigate - Router navigate, used by the "import" affordance.
 */
function notifyReplyContactResolutionError(
  e: unknown,
  fallbackMessage: string,
  navigate: (path: string) => void
): void {
  if (axios.isAxiosError(e) && e.response?.status === 404) {
    const senderEmail = (
      e.response.data as { sender_email?: string } | undefined
    )?.sender_email
    toast.error(
      <span>
        No CRM contact found{senderEmail ? ` for ${senderEmail}` : ''}.{' '}
        <button
          type="button"
          onClick={() => navigate('/contacts')}
          className="underline font-medium hover:no-underline"
        >
          Open Contacts to import
        </button>
        .
      </span>,
      { autoClose: 8000 }
    )
  } else {
    toast.error(fallbackMessage)
  }
}

interface Props {
  campaignId?: number
  className?: string
}

const READ_STATUS_SECTIONS: SectionedSection[] = [
  {
    key: 'read_status',
    items: [
      { label: 'Unread', value: 'unread' },
      { label: 'Read', value: 'read' },
    ],
  },
]

// Flat option list for the InlineSelectFilter (which takes `{label, value}[]`
// rather than sections). The read-status set is static; sender / campaign
// options are flattened from their section memos at the call site.
const READ_STATUS_OPTIONS = READ_STATUS_SECTIONS.flatMap((s) => s.items)

type Classification = 'all' | 'interested' | 'not_interested' | 'flagged'

const CLASSIFICATION_OPTIONS: {
  key: Classification
  label: string
}[] = [
  { key: 'all', label: 'All' },
  { key: 'interested', label: 'Interested' },
  { key: 'not_interested', label: 'Not Interested' },
  { key: 'flagged', label: 'Flagged' },
]

function ClassificationPills({
  selected,
  onChange,
  summary,
}: {
  selected: Classification
  onChange: (value: Classification) => void
  summary?: ReplySummary
}) {
  const getCount = (key: Classification): number | undefined => {
    if (!summary || !summary.is_available) return undefined
    switch (key) {
      case 'all':
        return summary.total
      case 'interested':
        return summary.interested
      case 'not_interested':
        return summary.not_interested
      case 'flagged':
        return summary.flagged
      default:
        return undefined
    }
  }

  const tabs = CLASSIFICATION_OPTIONS.map(({ key, label }) => {
    const count = getCount(key)
    return {
      key,
      title: (
        <>
          {label}
          {count !== undefined && (
            <span className="ml-1.5 font-normal">({count})</span>
          )}
        </>
      ),
    }
  })

  return (
    <PillTabs<Classification>
      aria-label="Filter replies by classification"
      idPrefix="reply-classification"
      size="sm"
      tabs={tabs}
      activeKey={selected}
      onChange={onChange}
    />
  )
}

/**
 * "Auto-reply" pill for automated replies (out-of-office auto-responders,
 * FIR-7537). Rendered next to the sender on automated messages in the
 * conversation view, and (via ReplyListItem's inline variant) on threads
 * whose every visible reply is automated. Delivery-failure notifications
 * never get this badge: bounce-only threads are hidden from the inbox
 * entirely and the conversation endpoint excludes failures.
 */
function AutoReplyBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
      <PiRobot className="size-3" />
      Auto-reply
    </span>
  )
}

function ReplyListSkeleton() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="w-full px-4 py-3.5 border-b border-neutral-100"
          aria-hidden
        >
          <div className="flex items-center justify-between mb-1.5">
            <div className="h-3 w-32 rounded bg-neutral-100" />
            <div className="h-2.5 w-10 rounded bg-neutral-100" />
          </div>
          <div className="h-2.5 w-24 rounded bg-neutral-100 mb-1.5" />
          <div className="h-2.5 w-full rounded bg-neutral-100 mb-2.5" />
          <div className="flex items-center gap-2">
            <div className="h-3 w-16 rounded-full bg-neutral-100" />
            <div className="h-2.5 w-12 rounded bg-neutral-100 ml-auto" />
          </div>
        </div>
      ))}
    </>
  )
}

function ReplyListItem({
  reply,
  isSelected,
  onClick,
}: {
  reply: Reply
  isSelected: boolean
  onClick: () => void
}) {
  const displayName =
    reply.lead_full_name || reply.from_name || reply.from_email_address

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative w-full text-left px-4 py-3.5 border-b border-gray-200 transition-colors',
        isSelected
          ? 'bg-gray-100'
          : !reply.is_read
            ? 'bg-brand-sage-50 hover:bg-brand-sage-100'
            : 'hover:bg-gray-50'
      )}
    >
      {/* Left accent bar: green for unread (replaces the old too-subtle dot),
          gray for the currently-selected (read) row. Unread wins so a new reply
          keeps its green signal even while selected. */}
      {(isSelected || !reply.is_read) && (
        <span
          className={cn(
            'absolute left-0 top-0 h-full w-1',
            !reply.is_read ? 'bg-success-bright' : 'bg-neutral-400'
          )}
          aria-hidden
        />
      )}
      {/* Row 1: name + timestamp */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={cn(
              'text-sm truncate',
              // Bump one weight up (medium, not bold) for unread OR the
              // currently-selected row; plain read rows stay normal.
              !reply.is_read || isSelected
                ? 'text-gray-900 font-medium'
                : 'text-gray-700'
            )}
          >
            {displayName}
          </span>
          {reply.thread_count > 1 && (
            <span className="text-xxs text-gray-600 shrink-0">
              ({reply.thread_count})
            </span>
          )}
        </div>
        <span className="text-xxs text-gray-600 shrink-0 ml-2">
          {reply.date_received ? formatRelativeTime(reply.date_received) : ''}
        </span>
      </div>

      {/* Row 2: company name */}
      {reply.lead_company && (
        <p className="text-xs text-gray-600 mb-0.5">{reply.lead_company}</p>
      )}

      {/* Row 3: body preview */}
      <p className="text-xs text-gray-700 truncate mb-2">{reply.text_body}</p>

      {/* Row 4: badges + emails sent */}
      <div className="flex items-center gap-2 flex-wrap">
        {reply.is_interested === true && (
          <span className="inline-flex items-center gap-1 text-sm px-1.5 py-0.5 rounded-full text-success-strong bg-success-bg-subtle">
            <PiStar className="size-3" />
            Interested
          </span>
        )}
        {/* Three-state classification (FIR-7589): false is an explicit
            operator verdict; unclassified threads (null) show no badge. */}
        {reply.is_interested === false && (
          <span
            className={cn(
              'inline-flex items-center gap-1 text-sm px-1.5 py-0.5 rounded-full text-gray-700',
              isSelected ? 'bg-white' : 'bg-gray-100'
            )}
          >
            <PiX className="size-3" />
            Not Interested
          </span>
        )}
        {reply.is_flagged && (
          <span className="inline-flex items-center gap-1 text-sm px-1.5 py-0.5 rounded-full text-warning-strong bg-warning-bg-subtle">
            <PiFlag className="size-3" />
            Flagged
          </span>
        )}
        {reply.is_automated_only && (
          <span
            className={cn(
              'inline-flex items-center gap-1 text-sm px-1.5 py-0.5 rounded-full text-gray-700',
              isSelected ? 'bg-white' : 'bg-gray-100'
            )}
          >
            <PiRobot className="size-3" />
            Auto-reply
          </span>
        )}
        <span className="text-sm text-gray-600 ml-auto flex items-center gap-1">
          <PiEnvelope className="size-3" />
          {reply.emails_sent_count != null && reply.emails_sent_count > 0
            ? `${reply.emails_sent_count} sent`
            : 'No emails sent'}
        </span>
      </div>
    </button>
  )
}

function ReplyDetailHeader({
  reply,
  currentIndex,
  totalCount,
  onPrev,
  onNext,
}: {
  reply: Reply
  currentIndex: number
  totalCount: number
  onPrev: () => void
  onNext: () => void
}) {
  const isFirst = currentIndex === 0
  const isLast = currentIndex === totalCount - 1

  return (
    <div className="flex items-center gap-2 px-[20px] py-2 border-b border-gray-200 bg-gray-50">
      <span className="inline-flex items-center text-sm px-2 py-0.5 rounded-full bg-neutral-200 text-neutral-800 shrink-0">
        Step {reply.emails_sent_count || 1}
      </span>
      <span className="text-sm text-gray-700 truncate min-w-0 flex-1">
        {reply.subject || '(no subject)'}
      </span>
      <span className="text-sm text-gray-600 shrink-0 ml-2">
        {currentIndex + 1} of {totalCount}
      </span>
      <button
        type="button"
        disabled={isFirst}
        onClick={onPrev}
        className={cn(
          'size-7 inline-flex items-center justify-center rounded transition-colors shrink-0',
          isFirst
            ? 'text-gray-400 cursor-not-allowed'
            : 'text-gray-700 hover:bg-gray-200'
        )}
      >
        <PiCaretLeft className="size-4" />
      </button>
      <button
        type="button"
        disabled={isLast}
        onClick={onNext}
        className={cn(
          'size-7 inline-flex items-center justify-center rounded transition-colors shrink-0',
          isLast
            ? 'text-gray-400 cursor-not-allowed'
            : 'text-gray-700 hover:bg-gray-200'
        )}
      >
        <PiCaretRight className="size-4" />
      </button>
    </div>
  )
}

/**
 * Slim header strip below `ReplyDetailHeader` that surfaces the linked CRM
 * contact-company so operators don't have to deep-link out of the inbox to
 * see who they're replying to (FIR-7419). Renders three affordances when
 * available — company page link, domain link, chain-wide door count — and
 * hides itself entirely when the reply has no resolved contact-company.
 */
function ReplyContactCompanyStrip({
  reply,
  onCreateDeal,
  onRequestSample,
  samplesEnabled,
}: {
  reply: Reply
  onCreateDeal: (threadId: number | null) => void
  onRequestSample: (threadId: number | null) => void
  samplesEnabled: boolean
}) {
  const buildCompanyUrl = useCompanyV2Url()
  const { data } = useThreadContactCompany(reply.thread_id)
  const company = data?.company
  const deal = data?.deal ?? null
  if (!company) return null

  // Conversion actions, promoted out of the kebab onto a labeled "Create Deal"
  // dropdown (FIR-7420). Create a deal is hidden once the thread already has a
  // linked deal (at most one deal per thread, enforced in CreateChainDealsMixin)
  // so the only affordance for an existing deal is the "Linked to <deal>" badge.
  // Request Sample is gated on the samples flag and stays independent of deals.
  const canCreateDeal = !deal
  const conversionItems: IDropdownItem[] = [
    {
      label: 'Create a deal',
      icon: <PiTrendUp size={16} />,
      callback: () => onCreateDeal(reply.thread_id),
      hidden: !canCreateDeal,
    },
    {
      label: 'Request Sample',
      icon: <PiHandCoins size={16} />,
      callback: () => onRequestSample(reply.thread_id),
      hidden: !samplesEnabled,
    },
  ]
  const visibleConversionItems = conversionItems.filter((item) => !item.hidden)

  const hasName = Boolean(company.name)
  const hasDomain = Boolean(company.domain)
  const hasDoors = company.door_count > 0
  if (!hasName && !hasDomain && !hasDoors && !deal) return null

  const companyHref = hasName
    ? buildCompanyUrl({
        company_type: company.company_type ?? undefined,
        chain_id: company.chain_id ?? undefined,
        university_id: company.university_id ?? undefined,
        k12district_id: company.k12district_id ?? undefined,
        lodging_id: company.lodging_id ?? undefined,
        id: company.id,
      })
    : null
  const showCompanyLink = companyHref && companyHref !== '-'

  return (
    <div className="flex items-center gap-3 px-[20px] py-2 border-b border-neutral-200 bg-white text-xs">
      {showCompanyLink && (
        <a
          href={companyHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-neutral-800 hover:text-brand-active transition-colors min-w-0"
          title="Open company page in a new tab"
        >
          <PiBuildings className="size-3.5 shrink-0 text-neutral-500" />
          <span className="truncate font-medium">{company.name}</span>
          <PiArrowSquareOut className="size-3 shrink-0 text-neutral-500" />
        </a>
      )}
      {hasDomain && (
        <a
          href={`https://${company.domain}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-neutral-500 hover:text-brand-active transition-colors min-w-0"
          title="Open website in a new tab"
        >
          <PiGlobe className="size-3.5 shrink-0" />
          <span className="truncate">{company.domain}</span>
          <PiArrowSquareOut className="size-3 shrink-0" />
        </a>
      )}
      {hasDoors && (
        <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5 text-neutral-700 shrink-0">
          <PiStorefront className="size-3" />
          {company.door_count} {company.door_count === 1 ? 'door' : 'doors'}
        </span>
      )}
      {deal && (
        <a
          href={`/deals/${deal.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 rounded-full bg-brand-subtle px-2 py-0.5 text-brand hover:text-brand-active transition-colors shrink-0 min-w-0"
          title="Open the linked deal in a new tab"
        >
          <PiLink className="size-3 shrink-0" />
          <span className="truncate">Linked to {deal.name || 'deal'}</span>
          <PiArrowSquareOut className="size-3 shrink-0" />
        </a>
      )}
      {visibleConversionItems.length > 0 && (
        <Dropdown
          align="end"
          items={visibleConversionItems}
          contentClassName={ds2026DropdownContentClass}
          itemClassName={ds2026DropdownItemClass}
          buttonComponent={
            <button
              type="button"
              className="ml-auto shrink-0 inline-flex items-center gap-1.5 h-7 px-1.5 rounded-md text-brand-bright text-sm font-medium transition-colors hover:text-brand-active data-[state=open]:text-brand-active focus:outline-none"
            >
              <span>Actions</span>
              <PiCaretDown className="size-3" />
            </button>
          }
        />
      )}
    </div>
  )
}

function ReplyClassificationRow({
  reply,
  onToggleInterested,
  onToggleFlag,
}: {
  reply: Reply
  onToggleInterested: () => void
  onToggleFlag: () => void
}) {
  return (
    <div className="shrink-0 px-[20px] py-3 border-t border-neutral-200">
      <div className="flex items-center gap-3 pl-[52px]">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Classification:</span>
          <button
            type="button"
            onClick={onToggleInterested}
            className={cn(
              'inline-flex items-center gap-1 text-sm px-2.5 py-1 rounded-full transition-colors',
              reply.is_interested === true
                ? 'text-success-strong bg-success-bg-subtle hover:brightness-95'
                : reply.is_interested === false
                  ? 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                  : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
            )}
          >
            {reply.is_interested === true ? (
              <>
                <PiStar className="size-3" />
                Interested
              </>
            ) : reply.is_interested === false ? (
              <>
                <PiX className="size-3" />
                Not Interested
              </>
            ) : (
              'Unclassified'
            )}
          </button>
        </div>
        <div className="w-px h-4 bg-line" />
        <button
          type="button"
          onClick={onToggleFlag}
          className={cn(
            'inline-flex items-center gap-1 text-sm px-2.5 py-1 rounded-full border transition-colors',
            reply.is_flagged
              ? 'text-warning-strong border-transparent bg-warning-bg-subtle'
              : 'text-gray-600 border-gray-300 hover:bg-gray-100'
          )}
        >
          <PiFlag className="size-3" />
          {reply.is_flagged ? 'Flagged' : 'Flag for Follow-up'}
        </button>
      </div>
    </div>
  )
}

interface ReplyActions {
  onMarkRead: (replyId: number, isRead: boolean) => void
  onMarkAutomated: (replyId: number, isAutomated: boolean) => void
  onUnsubscribe: (replyId: number) => void
  onDelete: (replyId: number) => void
}

/** Adapter: map a `Reply` to the shared `ThreadMessageData` shape. */
function replyToThreadMessageData(reply: Reply): ThreadMessageData {
  return {
    id: reply.id,
    sender: {
      name: reply.from_name || reply.lead_full_name || '',
      email: reply.from_email_address,
    },
    toRecipients: (reply.to_emails ?? []).map((r) => ({
      name: r.name || '',
      email: r.email_address,
    })),
    ccRecipients: (reply.cc_emails ?? []).map((r) => ({
      name: r.name || '',
      email: r.email_address,
    })),
    date: reply.date_received,
    htmlBody: reply.html_body,
    textBody: reply.text_body,
    attachments: (reply.attachments ?? []).map((a) => ({
      key: a.id,
      filename: a.file_name,
      // ReplyAttachment has no size/content_type on the current wire shape.
    })),
  }
}

/**
 * Build the kebab menu actions for a reply. Shared by ThreadMessage (in the
 * multi-message thread view) and the single-reply detail view so both expose
 * the same actions (Create deal, Mark read/unread, Unsubscribe, Delete, ...).
 */
function buildReplyMenuActions(reply: Reply, actions: ReplyActions) {
  // Conversion actions (Create deal / Request Sample) live on the
  // contact-company strip's Actions dropdown (FIR-7420), not the kebab.
  // The kebab is inbox-admin only: read state, automation, unsubscribe, delete.
  return [
    {
      label: reply.is_read ? 'Mark unread' : 'Mark read',
      // Icon shows the current state (open = already read); previously
      // followed the action's *target* state, which read backwards.
      icon: reply.is_read ? PiEnvelopeOpen : PiEnvelope,
      onClick: () => actions.onMarkRead(reply.id, !reply.is_read),
    },
    {
      label: reply.is_automated_reply ? 'Mark not automated' : 'Mark automated',
      icon: PiRobot,
      onClick: () =>
        actions.onMarkAutomated(reply.id, !reply.is_automated_reply),
    },
    {
      label: 'Unsubscribe contact',
      icon: PiUserMinus,
      onClick: () => actions.onUnsubscribe(reply.id),
    },
    {
      label: 'Delete reply',
      icon: PiTrash,
      onClick: () => actions.onDelete(reply.id),
      variant: 'destructive' as const,
    },
  ]
}

function ThreadMessage({
  reply,
  defaultExpanded,
  isLast,
  actions,
}: {
  reply: Reply
  defaultExpanded: boolean
  isLast: boolean
  actions: ReplyActions
}) {
  const menuItems = useMemo(
    () => buildReplyMenuActions(reply, actions),
    [reply, actions]
  )
  const data = useMemo(() => replyToThreadMessageData(reply), [reply])

  return (
    <ThreadMessageCard
      message={data}
      isLast={isLast}
      collapsible
      defaultExpanded={defaultExpanded}
      onExpand={() => {
        if (!reply.is_read) actions.onMarkRead(reply.id, true)
      }}
      menuItems={menuItems}
      senderBadge={reply.is_automated_reply ? <AutoReplyBadge /> : undefined}
    />
  )
}

function ThreadMessageList({
  replies,
  actions,
}: {
  replies: Reply[]
  actions: ReplyActions
}) {
  // Owns no scroll surface anymore (FIR-7419): the right-pane wraps thread
  // + composer in a single outer overflow-y-auto so the user can scroll
  // back to the original message while the composer is open.
  return (
    <div>
      {replies.map((reply, index) => (
        <ThreadMessage
          key={reply.id}
          reply={reply}
          defaultExpanded={index === replies.length - 1}
          isLast={index === replies.length - 1}
          actions={actions}
        />
      ))}
    </div>
  )
}

type ComposeMode = ReplyMode

/**
 * Whether the reply has enough distinct additional recipients that a
 * "Reply All" option is meaningful (beyond the workspace sender the reply
 * arrived on).
 */
function hasReplyAllRecipients(reply: Reply): boolean {
  return (
    filterSelf(
      [...(reply.to_emails ?? []), ...(reply.cc_emails ?? [])],
      reply.sender_email_address,
      (r) => r.email_address
    ).length > 0
  )
}

function ReplyActionBar({
  reply,
  onCompose,
  onTakeOver,
}: {
  reply: Reply
  onCompose: (mode: ComposeMode) => void
  onTakeOver: () => void
}) {
  const senderLabel = reply.sender_email_address?.split('@')[0] ?? 'outreach'
  const showReplyAll = hasReplyAllRecipients(reply)

  // Take-over needs a sendable mailbox -- an expired one would fail at send.
  const { data: integrationOptions } = useGetSendableEmailIntegrationOptions()
  const hasWorkEmail = useMemo(
    () => (integrationOptions ?? []).some((opt) => opt.value > 0),
    [integrationOptions]
  )

  return (
    <div className="px-[20px] py-4 border-t border-neutral-200">
      <div className="flex items-center gap-4 pl-[52px]">
        <button
          type="button"
          onClick={() => onCompose('forward')}
          className="inline-flex items-center gap-2 h-8 px-3 rounded-lg border border-ds2026-btn-outline-stroke bg-ds2026-btn-outline-bg text-sm text-ds2026-btn-outline-text hover:bg-ds2026-btn-outline-hover-bg transition-colors"
        >
          <PiArrowBendUpRight className="size-4" />
          Forward
        </button>
        {showReplyAll && (
          <button
            type="button"
            onClick={() => onCompose('reply_all')}
            className="inline-flex items-center gap-2 h-8 px-3 rounded-lg border border-ds2026-btn-outline-stroke bg-ds2026-btn-outline-bg text-sm text-ds2026-btn-outline-text hover:bg-ds2026-btn-outline-hover-bg transition-colors"
          >
            <PiArrowBendUpRight className="size-4 -scale-x-100" />
            Reply All
          </button>
        )}
        <button
          type="button"
          onClick={() => onCompose('reply')}
          className="inline-flex items-center gap-2 h-8 px-3 rounded-lg bg-brand-bright text-white text-sm hover:bg-brand-active transition-colors"
        >
          <PiPaperPlaneRight className="size-4" />
          Reply from @{senderLabel}
        </button>
        <button
          type="button"
          onClick={onTakeOver}
          disabled={!hasWorkEmail}
          title={
            hasWorkEmail
              ? undefined
              : 'To take over an email, you must connect your work email in Settings -> Email Integrations'
          }
          className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-bright hover:text-brand-active transition-colors disabled:text-neutral-400 disabled:cursor-not-allowed"
        >
          <PiArrowUpRight className="size-4" />
          Take over
        </button>
      </div>
    </div>
  )
}

function TakenOverPill({ reply }: { reply: Reply }) {
  const navigate = useNavigate()
  const buildCompanyUrl = useCompanyV2Url()

  // The "Open in inbox" CTA is only meaningful for users who can actually
  // see the take-over thread in their work mailbox. We approximate that
  // server-side check on the FE by matching `taken_over_by_email` against
  // the user's connected integration emails (own + shared) — the integration
  // option's `label` IS the email address.
  const { data: integrationOptions } = useGetEmailIntegrationOptions()
  const hasInboxAccess = useMemo(() => {
    const email = reply.taken_over_by_email?.toLowerCase()
    if (!email) return false
    return (integrationOptions ?? []).some(
      (opt) => opt.value > 0 && opt.label.toLowerCase() === email
    )
  }, [integrationOptions, reply.taken_over_by_email])

  const handleOpenInInbox = useCallback(async () => {
    if (reply.thread_id == null) {
      toast.error('Could not resolve the inbox link.')
      return
    }
    try {
      const link = await getReplyThreadInboxLink(reply.thread_id)
      const url = buildCompanyUrl(
        {
          company_type: link.contact_company.company_type ?? undefined,
          chain_id: link.contact_company.chain_id ?? undefined,
          university_id: link.contact_company.university_id ?? undefined,
          k12district_id: link.contact_company.k12district_id ?? undefined,
          lodging_id: link.contact_company.lodging_id ?? undefined,
          id: link.contact_company.id,
        },
        { email_thread_id: link.email_thread_id }
      )
      if (!url || url === '-') {
        toast.error('Could not resolve the inbox link.')
        return
      }
      navigate(url)
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 404) {
        toast.error(
          'No inbox thread linked yet. Try again in a moment or check your work email.'
        )
        return
      }
      toast.error('Could not resolve the inbox link.')
    }
  }, [buildCompanyUrl, navigate, reply.thread_id])

  return (
    <div className="px-[20px] py-4 border-t border-neutral-200">
      <div className="flex items-center gap-3 pl-[52px] h-8">
        <span className="inline-flex items-center gap-2 text-sm text-neutral-500">
          <PiArrowUpRight className="size-4 text-brand" />
          Taken over by{' '}
          <strong className="text-neutral-800">
            {reply.taken_over_by_name ?? 'someone'}
          </strong>
          {reply.taken_over_by_email && (
            <>
              {' '}
              with{' '}
              <strong className="text-neutral-800">
                {reply.taken_over_by_email}
              </strong>
            </>
          )}
        </span>
        <button
          type="button"
          onClick={handleOpenInInbox}
          disabled={!hasInboxAccess}
          title={
            hasInboxAccess ? undefined : "You don't have access to this inbox"
          }
          className="inline-flex items-center gap-1 text-sm text-brand hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
        >
          Open in inbox
          <PiCaretRight className="size-4" />
        </button>
      </div>
    </div>
  )
}

const composeSchema = z.object({
  body: z.string().min(1, 'Message body is required'),
})

type ComposeFormValues = z.infer<typeof composeSchema>

// Inline styles for the reply-composer template controls: they render INSIDE
// Quill's toolbar, whose bundled CSS forces width/height/border/float on
// descendant buttons via higher-specificity selectors than Tailwind utilities.
// Inline styles win. Colors pull from the design tokens (not raw hex) so they
// track the palette. (Mirrors the sequence builder's "Variables" trigger.)
const REPLY_TPL_PILL_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  width: 'auto',
  height: '28px',
  padding: '0 10px',
  whiteSpace: 'nowrap',
  float: 'none',
  flexShrink: 0,
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: colors['neutral-300'], // DS2026 outline stroke
  borderRadius: '6px',
  background: colors.surface,
  color: colors['neutral-800'],
  fontFamily: 'Inter, sans-serif',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
}

const REPLY_TPL_ICON_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '28px',
  height: '28px',
  float: 'none',
  flexShrink: 0,
  // Longhand borderColor (not the `border` shorthand): this button toggles
  // `disabled` which overrides borderColor, and mixing shorthand + longhand
  // makes React drop the color on the enable transition, so the border would
  // fall back to `currentColor` (#4b5563) — the "dark outline" bug. Keeping it
  // a longhand matches the Templates pill's Gray-300 stroke consistently.
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: colors['neutral-300'],
  borderRadius: '6px',
  background: colors.surface,
  color: colors['neutral-600'],
  cursor: 'pointer',
}

/** "Templates" browse pill + "Save reply as template" icon for the composer
 *  toolbar (FIR-7582). */
function ReplyComposerTemplateButtons({
  onBrowse,
  onSave,
  saveDisabled,
}: {
  onBrowse: () => void
  onSave: () => void
  saveDisabled: boolean
}) {
  return (
    <div
      className="flex items-center gap-2"
      style={{ fontFamily: 'Inter, sans-serif' }}
    >
      <span aria-hidden className="h-5 w-px bg-line" />
      <button
        type="button"
        onClick={onBrowse}
        title="Browse reply templates"
        style={REPLY_TPL_PILL_STYLE}
      >
        <PiStack
          style={{ width: 16, height: 16, flexShrink: 0, float: 'none' }}
        />
        <span>Templates</span>
      </button>
      <button
        type="button"
        onClick={onSave}
        disabled={saveDisabled}
        title={
          saveDisabled
            ? 'Write a reply to save it as a template'
            : 'Save reply as a template'
        }
        aria-label="Save reply as a template"
        style={{
          ...REPLY_TPL_ICON_STYLE,
          ...(saveDisabled
            ? {
                color: colors['neutral-400'],
                borderColor: colors['neutral-200'],
                cursor: 'not-allowed',
              }
            : {}),
        }}
      >
        <PiFloppyDisk
          style={{ width: 16, height: 16, flexShrink: 0, float: 'none' }}
        />
      </button>
    </div>
  )
}

interface ReplyComposeHandle {
  /** Replace the composer body in place — no remount, so Cc/Bcc/forward-to
   *  recipients the rep already entered are preserved (FIR-7582 review). */
  setBody: (html: string) => void
}

interface ReplyComposePanelProps {
  reply: Reply
  mode: ComposeMode
  /**
   * When set, the reply is sent via the operator's connected Nylas mailbox
   * (FIR-6879 spike). When null/undefined, the reply goes through the
   * EmailBison alias as before.
   */
  integrationId?: number | null
  /**
   * On mount, this element is scrolled into view inside the right-pane's
   * single scroll surface so the user lands at the composer but can scroll
   * up to re-read the thread (FIR-7419).
   */
  scrollIntoViewRef?: (node: HTMLDivElement | null) => void
  onDiscard: () => void
  onSent: () => void
  /** Called from the inline-header mode switcher (Reply / Reply All / Forward). */
  onModeChange?: (mode: ReplyMode) => void
  /** Open the reply-templates browse drawer (parent owns it). */
  onBrowseTemplates?: () => void
  /** Save the current draft as a template — receives the live body HTML. */
  onSaveAsTemplate?: (bodyHtml: string) => void
}

const ReplyComposePanel = forwardRef<
  ReplyComposeHandle,
  ReplyComposePanelProps
>(function ReplyComposePanel(
  {
    reply,
    mode,
    integrationId,
    scrollIntoViewRef,
    onDiscard,
    onSent,
    onModeChange,
    onBrowseTemplates,
    onSaveAsTemplate,
  },
  ref
) {
  const replyMutation = useReplyToReply()
  const forwardMutation = useForwardReply()
  const workEmailMutation = useTakeOver()
  const useWorkEmail = mode === 'reply' && integrationId != null
  const { data: integrationOptions } = useGetEmailIntegrationOptions()
  const workEmailLabel = useMemo(
    () => integrationOptions?.find((opt) => opt.value === integrationId)?.label,
    [integrationOptions, integrationId]
  )
  const { user } = useAuth()
  const userInitials =
    [user?.first_name, user?.last_name]
      .filter(Boolean)
      .map((n) => n![0].toUpperCase())
      .join('')
      .slice(0, 2) || 'UN'

  // Lazy initializers so reply_all's Cc pre-populate runs once at mount,
  // not on every render of the compose panel.
  const [forwardTo, setForwardTo] = useState<RecipientOption[]>([])
  const [ccTo, setCcTo] = useState<RecipientOption[]>(() =>
    mode === 'reply_all'
      ? buildReplyAllCcOptions(
          reply.to_emails ?? [],
          reply.cc_emails ?? [],
          reply.sender_email_address
        )
      : []
  )
  const [bccTo, setBccTo] = useState<RecipientOption[]>([])
  // The reply-all header summary surfaces the pre-populated Cc recipients, so
  // the editable Cc row starts collapsed (FIR-7804) — the user reveals it via
  // the Cc toggle or by clicking the summary. Forward/reply have no default Cc.
  const [showCc, setShowCc] = useState(false)
  const [showBcc, setShowBcc] = useState(false)

  // Thread-context contacts surfaced in autocomplete regardless of search.
  const extraContacts = useMemo(
    () =>
      [...(reply.to_emails ?? []), ...(reply.cc_emails ?? [])].map((r) => ({
        email: r.email_address,
        name: r.name,
      })),
    [reply.to_emails, reply.cc_emails]
  )

  const {
    options: recipientOptions,
    isLoading: isLoadingRecipients,
    setSearch: setRecipientSearch,
  } = useRecipientOptions(extraContacts, false)

  const { control, handleSubmit, watch, getValues, setValue } =
    useForm<ComposeFormValues>({
      defaultValues: { body: '' },
      resolver: zodResolver(composeSchema),
      mode: 'onSubmit',
    })

  // Let the parent drop a template body into the live form without remounting
  // (a remount would reset the local Cc/Bcc/forward-to recipient state).
  useImperativeHandle(
    ref,
    () => ({ setBody: (html: string) => setValue('body', html) }),
    [setValue]
  )
  // A blank Quill body serializes to "<p><br></p>"; strip tags to detect empty.
  const hasDraftBody =
    watch('body')
      .replace(/<[^>]*>/g, '')
      .trim().length > 0

  const isSending =
    replyMutation.isPending ||
    forwardMutation.isPending ||
    workEmailMutation.isPending

  const onSubmit = handleSubmit((values) => {
    const cc_emails = ccTo.length ? toWireRecipients(ccTo) : undefined
    const bcc_emails = bccTo.length ? toWireRecipients(bccTo) : undefined

    // Quill 2.0.3's getSemanticHTML() encodes every space as `&nbsp;`
    // (quill/core/editor.js does `replaceAll(' ', '&nbsp;')`), so the
    // composer body arrives with a non-breaking space between every word.
    // Sent as-is, the email never wraps and renders oddly (FIR-7571).
    // Unlike the sequence-step path, these inline replies don't round-trip
    // through the email-render layer that strips NBSP, so normalize here.
    // Only the entity form is the serializer artifact: a genuine U+00A0
    // round-trips as the raw character, not the entity, so this is exact.
    const message = values.body.replaceAll('&nbsp;', ' ')

    if (mode === 'forward') {
      if (!forwardTo.length) return
      const payload: ForwardReplyPayload = {
        message,
        to_emails: toWireRecipients(forwardTo),
        cc_emails,
        bcc_emails,
      }
      forwardMutation.mutate(
        { replyId: reply.id, payload },
        { onSuccess: onSent }
      )
      return
    }

    if (useWorkEmail && integrationId != null) {
      const payload: TakeOverPayload = {
        integration_id: integrationId,
        message,
        cc_emails,
        bcc_emails,
      }
      if (reply.thread_id == null) {
        toast.error('Could not resolve the reply thread.')
        return
      }
      workEmailMutation.mutate(
        { threadId: reply.thread_id, payload },
        {
          onSuccess: () => {
            toast.success(`Sent from ${workEmailLabel ?? 'your work email'}`)
            onSent()
          },
          onError: (err) => {
            const detail = axios.isAxiosError(err)
              ? (err.response?.data as { detail?: string } | undefined)?.detail
              : undefined
            toast.error(detail ?? 'Could not send via your work email.')
          },
        }
      )
      return
    }

    const payload: ReplyToReplyPayload = {
      message,
      cc_emails,
      bcc_emails,
    }
    replyMutation.mutate({ replyId: reply.id, payload }, { onSuccess: onSent })
  })

  const baseSelectProps = useMemo(
    () => ({
      // Match the company-inbox compose treatment (ComposePanel): DS2026 gray
      // menu + Phosphor caret/remove/clear, compact 32px rows.
      ds2026: true as const,
      height: '32px',
      isMulti: true as const,
      options: recipientOptions,
      // react-select's native event — the inbox's CreatableSelect wrapper
      // renames this to `onSearchChange`, but we use ReactCreatableSelectWrapper
      // directly here, which forwards unknown props to react-select as-is.
      onInputChange: (input: string) => setRecipientSearch(input),
      isLoading: isLoadingRecipients,
      styles: emailRecipientStyles,
      formatOptionLabel: formatEmailRecipientOption,
      isValidNewOption: looksLikeEmail,
    }),
    [recipientOptions, isLoadingRecipients, setRecipientSearch]
  )

  /** Build a recipient row for To/Cc/Bcc — identical shape across the three. */
  const renderRow = (
    label: 'To' | 'Cc' | 'Bcc',
    value: RecipientOption[],
    setValue: React.Dispatch<React.SetStateAction<RecipientOption[]>>,
    opts: { onRemove?: () => void; trailing?: React.ReactNode } = {}
  ) => (
    <RecipientRow
      label={label}
      onRemove={opts.onRemove}
      trailing={opts.trailing}
    >
      <ReactCreatableSelectWrapper
        {...baseSelectProps}
        placeholder=""
        value={value}
        onChange={(val: unknown) => setValue((val ?? []) as RecipientOption[])}
        onCreateOption={(input: string) =>
          setValue((prev) => [...prev, { label: input, value: input }])
        }
      />
    </RecipientRow>
  )

  // Null when both toggles are already revealed so `RecipientRow` doesn't
  // render an empty trailing-slot wrapper that still eats `gap-2`.
  const toggles =
    showCc && showBcc ? null : (
      <CcBccToggles
        showCc={showCc}
        showBcc={showBcc}
        onShowCc={() => setShowCc(true)}
        onShowBcc={() => setShowBcc(true)}
      />
    )

  const baseLabel = `Replying to ${reply.from_name || reply.from_email_address}`
  const inlineLabel = useWorkEmail
    ? `${baseLabel} from ${workEmailLabel ?? 'your work email'}`
    : baseLabel

  // Reply-All header used to show the thread subject, which hid the To
  // recipient — the person being replied to — entirely (FIR-7804). Surface the
  // actual recipients Gmail-style instead: the original sender (the implicit
  // To) plus the Cc recipients, collapsing the tail into "+N more". The Cc
  // people drop out of the summary once their editable row is open so they
  // aren't listed twice.
  const summaryRecipients: SummaryRecipient[] = [
    { name: reply.from_name ?? '', email: reply.from_email_address },
    ...(showCc ? [] : ccTo.map((opt) => parseEmailOption(opt))),
  ]
  const headerLabel =
    mode === 'reply_all' ? (
      <InlineReplyRecipients
        recipients={summaryRecipients}
        // No-op once the Cc row is open — drop the handler so the summary stops
        // rendering as an interactive button (no misleading cursor/hover).
        onExpand={showCc ? undefined : () => setShowCc(true)}
      />
    ) : (
      inlineLabel
    )

  return (
    <div ref={scrollIntoViewRef}>
      <InlineComposeShell userInitials={userInitials} flat>
        {useWorkEmail && (
          <div className="flex items-center gap-2 px-3 py-1.5 text-xs text-neutral-500 bg-brand-subtle border-b border-neutral-200">
            <PiArrowUpRight className="size-3 text-brand" />
            <span>Taking over</span>
          </div>
        )}
        {mode !== 'forward' && (
          <InlineReplyHeader
            mode={mode}
            label={headerLabel}
            onModeChange={onModeChange}
            trailing={toggles}
          />
        )}

        <div className="px-3 pt-3 flex flex-col">
          {mode === 'forward' &&
            renderRow('To', forwardTo, setForwardTo, { trailing: toggles })}

          {showCc &&
            renderRow('Cc', ccTo, setCcTo, {
              onRemove: () => {
                setShowCc(false)
                setCcTo([])
              },
            })}

          {showBcc &&
            renderRow('Bcc', bccTo, setBccTo, {
              onRemove: () => {
                setShowBcc(false)
                setBccTo([])
              },
            })}

          <StyledEditorRHF
            name="body"
            control={control}
            placeholder="Write your message..."
            borderless
            // FIR-7419: target the inner `.ql-editor` for the bigger click
            // surface — `min-h` on the outer wrapper doesn't propagate to
            // Quill's own root. Outer wrapper grows with the editor so the
            // entire card stays clickable. Placeholder is pinned to the
            // editor's `!py-2 !px-2` padding so the prompt sits exactly
            // where typed text begins.
            className={cn(
              'flex flex-col',
              '[&_.ql-editor]:!py-2 [&_.ql-editor]:!px-2',
              '[&_.ql-editor]:!min-h-[200px]',
              '[&_.ql-editor]:flex-1',
              // Match the company-inbox compose body: 14px typed text and hint
              // (Quill's default ~13px makes the placeholder read too small).
              '[&_.ql-editor]:!text-base',
              '[&_.ql-editor.ql-blank::before]:!text-base',
              // Match the campaign sequence-step editor spacing
              // (FIR-7341 follow-up): zero paragraph margin so each Enter
              // is a tight single line break instead of the `Editor.css`
              // default `<p>{margin-bottom:1em}` double-spaced gap.
              '[&_.ql-editor_p]:!mb-0',
              '[&_.ql-editor.ql-blank::before]:!top-2',
              '[&_.ql-editor.ql-blank::before]:!left-2'
            )}
            additionalToolbarButtons={
              onBrowseTemplates && onSaveAsTemplate ? (
                <ReplyComposerTemplateButtons
                  onBrowse={onBrowseTemplates}
                  onSave={() => onSaveAsTemplate(getValues('body'))}
                  saveDisabled={!hasDraftBody}
                />
              ) : undefined
            }
            toolbarSuffix={
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onDiscard}
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-md text-sm text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 transition-colors"
                >
                  Discard
                </button>
                <button
                  type="button"
                  onClick={onSubmit}
                  disabled={isSending}
                  className={cn(
                    'inline-flex items-center gap-1.5 h-8 px-4 rounded-md text-sm font-medium text-white transition-colors',
                    isSending
                      ? 'bg-brand-bright/60 cursor-not-allowed'
                      : 'bg-brand-bright hover:bg-brand-active'
                  )}
                >
                  <PiPaperPlaneRight className="size-4" />
                  {isSending ? 'Sending...' : 'Send'}
                </button>
              </div>
            }
            toolbarClassName="sticky bottom-0 bg-white z-10"
          />
        </div>
      </InlineComposeShell>
    </div>
  )
})
ReplyComposePanel.displayName = 'ReplyComposePanel'

export function CampaignRepliesTab({ campaignId, className }: Props) {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedThreadId, setSelectedThreadId] = useState<number | null>(null)
  const [selectedThreadContext, setSelectedThreadContext] = useState<{
    leadId: number | null
    campaignId: number | null
  }>({ leadId: null, campaignId: null })
  const [senderFilter, setSenderFilter] = useState<string[]>([])
  const [campaignFilter, setCampaignFilter] = useState<string[]>([])
  const [readFilter, setReadFilter] = useState<string[]>([])
  const [classificationFilter, setClassificationFilter] =
    useState<Classification>('all')
  const [composeMode, setComposeMode] = useState<ComposeMode | null>(null)
  const [composeIntegrationId, setComposeIntegrationId] = useState<
    number | null
  >(null)
  // Reply-templates (FIR-7582): browse drawer + save-as-template modal.
  // Inserting a template goes through the composer panel's imperative `setBody`
  // handle (no remount → Cc/Bcc/forward-to preserved).
  const [replyTemplatesOpen, setReplyTemplatesOpen] = useState(false)
  const [saveTemplateState, setSaveTemplateState] = useState<{
    open: boolean
    body: string
  }>({ open: false, body: '' })
  const replyComposeRef = useRef<ReplyComposeHandle>(null)
  // The reply-templates sheet dims the whole page content (not just this tab),
  // so it's portaled into the nearest FBPage container. Captured via a stable
  // ref callback (falls back to the tab root when there's no FBPage ancestor,
  // e.g. rendered standalone).
  const [sheetPortalEl, setSheetPortalEl] = useState<HTMLElement | null>(null)
  const captureSheetPortalEl = useCallback((node: HTMLDivElement | null) => {
    setSheetPortalEl(
      node
        ? ((node.closest('[data-fb-page]') as HTMLElement | null) ?? node)
        : null
    )
  }, [])
  // Reply id whose Take Over modal is open. Null when the modal is dismissed.
  const [takeOverTargetReplyId, setTakeOverTargetReplyId] = useState<
    number | null
  >(null)
  // Reply id whose "Different Address" mismatch modal is open. Independent
  // of `takeOverTargetReplyId` so the affordances don't conflict.
  const [mismatchTargetReplyId, setMismatchTargetReplyId] = useState<
    number | null
  >(null)

  // Deep-link from the "interested reply" notification email. Captured once
  // on mount so URL clears (below) don't re-trigger the narrow. Owned by
  // this component (not the parent dashboard page) so dismissing actually
  // sticks when the user toggles back to this tab.
  const [searchParams, setSearchParams] = useSearchParams()
  const initialLinkedThreadId = useMemo<number | null>(() => {
    const raw = searchParams.get('thread')
    if (!raw) return null
    const n = Number(raw)
    return Number.isFinite(n) && n > 0 ? n : null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [linkedThreadId, setLinkedThreadId] = useState<number | null>(
    initialLinkedThreadId
  )
  // Set true after we resolve the thread one way or another (found or
  // toasted-as-missing) so we don't re-toast on every render.
  const linkedThreadHandledRef = useRef(false)
  const openCompose = useCallback(
    (mode: ComposeMode, integrationId: number | null = null) => {
      setComposeMode(mode)
      setComposeIntegrationId(mode === 'reply' ? integrationId : null)
    },
    []
  )
  const closeCompose = useCallback(() => {
    setComposeMode(null)
    setComposeIntegrationId(null)
  }, [])

  // Callback ref handed to ReplyComposePanel: when the panel mounts inside
  // the right-pane's single scroll surface (FIR-7419), bring it into view
  // so the user lands at the composer ready to type, with the thread still
  // scrollable above it.
  const scrollComposerIntoView = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      node.scrollIntoView({ block: 'start', behavior: 'smooth' })
    }
  }, [])

  const [debouncedSearch] = useDebounce(searchQuery, 300)

  // When campaignId is provided, resolve bisonCampaignId from the sequence
  const { data: sequence, isLoading: isLoadingSequence } =
    useGetCampaignSequence(campaignId ?? 0)
  const bisonCampaignId = campaignId
    ? (sequence?.bison_campaign_id ?? 0)
    : undefined

  const clearLinkedThread = useCallback(() => {
    setLinkedThreadId(null)
    linkedThreadHandledRef.current = true
    // Strip ?thread= so refresh / re-mount doesn't re-narrow.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('thread')
        return next
      },
      { replace: true }
    )
  }, [setSearchParams])

  // Base filters from the top-line dropdowns (used for both list and summary).
  // When a deep-link thread is active, force thread_id so the list narrows
  // to that single thread and ignores other filters; clearing the banner
  // restores normal dropdown behaviour.
  const baseFilters = useMemo<ReplyFilters>(() => {
    const f: ReplyFilters = {}
    if (bisonCampaignId !== undefined) f.campaign_id = bisonCampaignId
    if (campaignFilter.length)
      f.campaign = campaignFilter.map((id) => Number(id))
    if (debouncedSearch) f.search = debouncedSearch
    if (senderFilter.length) f.sender_email = senderFilter
    if (readFilter.length === 1) f.is_read = readFilter[0] === 'read'
    if (linkedThreadId !== null) f.thread_id = linkedThreadId
    return f
  }, [
    bisonCampaignId,
    campaignFilter,
    debouncedSearch,
    senderFilter,
    readFilter,
    linkedThreadId,
  ])

  // Full filters including classification pill (used for the list only)
  const filters = useMemo<ReplyFilters>(() => {
    const f = { ...baseFilters }
    if (classificationFilter === 'interested') f.is_interested = true
    if (classificationFilter === 'not_interested') f.is_interested = false
    if (classificationFilter === 'flagged') f.is_flagged = true
    return f
  }, [baseFilters, classificationFilter])

  const {
    data: repliesData,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetReplyThreadsInfinite(filters)
  const { data: summaryData } = useGetReplyThreadSummary(baseFilters)
  const { data: senderEmailsData } = useGetWorkspaceSenderEmails()
  const { data: campaignsData } = useGetCampaigns()

  const senderEmailSections = useMemo<SectionedSection[]>(
    () => [
      {
        key: 'sender_emails',
        items: (senderEmailsData?.results ?? []).map((se) => ({
          label: se.name ? `${se.name} (${se.email})` : se.email,
          value: se.email,
        })),
      },
    ],
    [senderEmailsData]
  )

  const campaignSections = useMemo<SectionedSection[]>(
    () => [
      {
        key: 'campaigns',
        items: (campaignsData?.results ?? []).map((c) => ({
          label: c.name,
          value: String(c.id),
        })),
      },
    ],
    [campaignsData]
  )

  // Flattened single-section option lists for the InlineSelectFilter triggers.
  const senderEmailOptions = useMemo(
    () => senderEmailSections.flatMap((s) => s.items),
    [senderEmailSections]
  )
  const campaignOptions = useMemo(
    () => campaignSections.flatMap((s) => s.items),
    [campaignSections]
  )

  const markReplyRead = useMarkReplyRead()
  const toggleFlag = useToggleThreadFlag()
  const markInterested = useMarkReplyInterested()
  const markAutomated = useMarkReplyAutomated()
  const unsubscribeContact = useUnsubscribeReplyContact()
  const deleteReply = useDeleteReply()
  // Signed-in user → prefilled as the deal's account owner (FIR-7420).
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const featureFlag = useFeatureFlagService()

  const replies = useMemo(
    () => repliesData?.pages.flatMap((p) => p.results) ?? [],
    [repliesData]
  )
  // Mirror the latest replies into a ref so callbacks below can read the
  // current page without taking `replies` as a dep (keeps `replyActions`
  // memo-stable across scroll-driven re-renders).
  const repliesRef = useRef<Reply[]>(replies)
  repliesRef.current = replies

  // Create Deal modal context (resolved lazily when the user opens it).
  const [createDealContext, setCreateDealContext] =
    useState<ThreadContactCompany | null>(null)
  // Request Sample modal context — opened from the same strip Actions
  // dropdown as Create Deal (FIR-7420).
  const [sampleRequestContext, setSampleRequestContext] =
    useState<SampleRequestContext | null>(null)

  const handleCreateDeal = useCallback(
    async (threadId: number | null) => {
      if (threadId == null) return
      try {
        const ctx = await getThreadContactCompany(threadId)
        if (!ctx.company_type_slug) {
          // Without a sub-category slug we can't pick the right Create Deal
          // form. Bail rather than opening the modal against a wrong default
          // (e.g. hospital company rendered as restaurants-and-bars).
          toast.error(
            'This company has no type set. Update the company type before creating a deal.'
          )
          return
        }
        setCreateDealContext(ctx)
      } catch (e) {
        notifyReplyContactResolutionError(
          e,
          'Could not open Create Deal. Please try again.',
          navigate
        )
      }
    },
    [navigate]
  )

  const handleRequestSample = useCallback(
    async (threadId: number | null) => {
      if (threadId == null) return
      try {
        const ctx = await getThreadContactCompany(threadId)
        // Seed the wizard's contact step from the thread's reply sender
        // (FIR-7419) so the rep doesn't retype it. The thread endpoint
        // returns no sender name/email, so pull it from the loaded reply.
        const reply = repliesRef.current.find((r) => r.thread_id === threadId)
        const { firstName, lastName } = splitDisplayName(reply?.from_name)
        setSampleRequestContext({
          contactCompanyId: ctx.contact_company_id,
          contact: {
            firstName,
            lastName,
            email: reply?.from_email_address ?? '',
          },
        })
      } catch (e) {
        notifyReplyContactResolutionError(
          e,
          'Could not open Request Sample. Please try again.',
          navigate
        )
      }
    },
    [navigate]
  )

  const replyActions: ReplyActions = useMemo(
    () => ({
      onMarkRead: (replyId, isRead) =>
        markReplyRead.mutate({ replyId, isRead }),
      onMarkAutomated: (replyId, isAutomated) =>
        markAutomated.mutate({ replyId, isAutomated }),
      onUnsubscribe: async (replyId) => {
        const ok = await confirm(
          'Are you sure you want to unsubscribe this contact? They will no longer receive emails from this campaign.',
          'Unsubscribe Contact'
        )
        if (ok) unsubscribeContact.mutate(replyId)
      },
      onDelete: async (replyId) => {
        const ok = await confirm(
          'This reply will be permanently deleted.',
          'Delete Reply',
          '',
          { confirmLabel: 'Delete', buttonVariant: 'danger' }
        )
        if (ok) deleteReply.mutate(replyId)
      },
    }),
    [markReplyRead, markAutomated, unsubscribeContact, deleteReply]
  )
  const currentSelectedReply = replies.find(
    (r) => r.thread_id === selectedThreadId
  )
  // Keep last valid selection during refetches so the UI doesn't flash blank
  const selectedReplyRef = useRef<Reply | undefined>(undefined)
  if (currentSelectedReply) {
    selectedReplyRef.current = currentSelectedReply
  } else if (!selectedThreadId) {
    selectedReplyRef.current = undefined
  }
  const selectedReply = currentSelectedReply ?? selectedReplyRef.current

  // Insert a reply template into the composer: resolve its {VARIABLE} merge
  // tokens against the selected reply's contact and drop the HTML into the
  // composer via the imperative handle (no remount → Cc/Bcc preserved).
  // escapeValues=true because lead name/company/email are external CRM free
  // text going into an HTML body — unescaped `<`/`>`/`&` would corrupt the
  // markup. The templates drawer is only reachable from an open composer, so
  // the ref is always attached by the time this fires.
  const handleUseReplyTemplate = useCallback(
    (template: ReplyTemplate) => {
      const tokens = buildContactTokens({
        full_name: selectedReply?.lead_full_name,
        email: selectedReply?.lead_email,
        company_name: selectedReply?.lead_company,
      })
      const filled = fillVariables(template.body, tokens, {
        escapeValues: true,
      })
      replyComposeRef.current?.setBody(filled)
      setReplyTemplatesOpen(false)
    },
    [selectedReply]
  )

  const handleSaveReplyTemplate = useCallback((bodyHtml: string) => {
    setSaveTemplateState({ open: true, body: bodyHtml })
  }, [])

  // Stable menu data for the single-reply detail card, so `ThreadMessageCard`
  // memoization isn't defeated by fresh object identities on every
  // CampaignRepliesTab render (scroll, filter, compose toggle, ...).
  // `singleReplyData` is built below, once `threadReplies` is available.
  const singleReplyMenu = useMemo(
    () =>
      selectedReply
        ? buildReplyMenuActions(selectedReply, replyActions)
        : undefined,
    [selectedReply, replyActions]
  )

  // "Different Address" badge — backend stamps `from_matches_contact` /
  // `contact_id` on the reply via prefetched data, so the badge renders
  // without an extra per-reply fetch. Modal lazy-loads the contact detail
  // (name/title/email slots) only when actually opened.
  const showMismatchBadge =
    selectedReply != null &&
    selectedReply.contact_id != null &&
    selectedReply.from_matches_contact === false
  // Single-reply detail card: the selected row is a thread row whose
  // is_automated_reply mirrors the latest visible reply, so on a one-message
  // thread it IS the message's automation state (FIR-7537).
  const showAutoReplyBadge = selectedReply?.is_automated_reply === true
  const senderBadge =
    showMismatchBadge || showAutoReplyBadge ? (
      <span className="inline-flex items-center gap-1.5">
        {showAutoReplyBadge && <AutoReplyBadge />}
        {showMismatchBadge && (
          <Tooltip
            content="This reply is from an email not on this contact. Click to add or override."
            placement="top"
          >
            <button
              type="button"
              onClick={() =>
                selectedReply && setMismatchTargetReplyId(selectedReply.id)
              }
              className="inline-flex items-center gap-1 rounded-full border border-warning-border bg-warning-bg-subtle px-2 py-0.5 text-xs text-warning-strong hover:bg-warning-bg"
            >
              <PiWarning className="size-3" />
              Different Address
            </button>
          </Tooltip>
        )}
      </span>
    ) : undefined

  // Fetch all replies in the selected thread (uses stable context, not derived from selectedReply)
  const { data: threadData } = useGetThreadReplies(
    selectedThreadContext.leadId,
    selectedThreadContext.campaignId
  )
  const threadReplies = useMemo(
    () =>
      [...(threadData?.results ?? [])].sort(
        (a, b) =>
          new Date(a.date_received).getTime() -
          new Date(b.date_received).getTime()
      ),
    [threadData]
  )

  // Detail card source: prefer the full reply from the thread fetch, which
  // carries `html_body`. The selection row comes from the reply-thread LIST
  // endpoint, whose serializer returns `html_body: null` by design — so
  // rendering the single-message detail card straight from that row drops the
  // HTML and falls back to the plain-text body: quoted From/To/Subject headers
  // lose their bold, and the sender's hidden email preheader (display:none in
  // the HTML) surfaces as a visible duplicated line. Fall back to the list row
  // only while the thread fetch is in flight.
  const detailReply = useMemo(
    () => pickDetailReply(threadReplies, selectedReply),
    [threadReplies, selectedReply]
  )
  const singleReplyData = useMemo(
    () => (detailReply ? replyToThreadMessageData(detailReply) : undefined),
    [detailReply]
  )

  // Classification verdicts belong to the prospect's message, not our own
  // outbound replies — see pickClassificationReply (FIR-7589).
  const classificationReply = useMemo(
    () => pickClassificationReply(threadReplies, selectedReply),
    [threadReplies, selectedReply]
  )

  const { containerRef, sentinelRef } = useInfiniteScroll({
    onLoadMore: fetchNextPage,
    isLoading: isFetchingNextPage,
    hasMore: hasNextPage,
  })

  const handleSelectReply = useCallback(
    (reply: Reply) => {
      setSelectedThreadId(reply.thread_id)
      setSelectedThreadContext({
        leadId: reply.lead_id,
        campaignId: reply.campaign_id,
      })
      closeCompose()
      if (!reply.is_read) {
        markReplyRead.mutate({ replyId: reply.id, isRead: true })
      }
    },
    [markReplyRead, closeCompose]
  )

  // The narrowed thread row, when the deep-link is active. Drives the banner
  // copy and the auto-select. Pulled from the (already-fetched, narrowed)
  // list so we don't need a separate side-fetch.
  const linkedThreadRow =
    linkedThreadId !== null
      ? (replies.find((r) => r.thread_id === linkedThreadId) ?? null)
      : null

  // Once the narrowed list has actually returned a response, decide what to
  // do. Gate on `repliesData !== undefined` so we don't treat a not-yet-
  // enabled query (e.g. while waiting for the sequence to resolve
  // bisonCampaignId) as a settled empty result.
  //   - row found: leave the banner up; auto-select fires below.
  //   - empty result: toast and clear the link so the list goes back to all
  //     replies. handledRef prevents toast spam on every render.
  if (
    linkedThreadId !== null &&
    !linkedThreadHandledRef.current &&
    repliesData !== undefined
  ) {
    if (linkedThreadRow) {
      linkedThreadHandledRef.current = true
    } else if (replies.length === 0) {
      linkedThreadHandledRef.current = true
      queueMicrotask(() => {
        toast.info("That thread isn't available — showing all replies.")
        clearLinkedThread()
      })
    }
  }

  // One-shot deep-link auto-select: once the narrowed list has the thread,
  // open it. The ref keeps it from re-firing on refetch; we also skip when
  // the user has already picked a thread (race on slow networks). Setting
  // the ref before the microtask is intentional — strict-mode double renders
  // would otherwise queue two side-effect calls. Done inline (not via
  // useEffect) per `client/CLAUDE.md`.
  const didAutoSelectRef = useRef(false)
  if (
    linkedThreadRow !== null &&
    !didAutoSelectRef.current &&
    selectedThreadId === null
  ) {
    didAutoSelectRef.current = true
    queueMicrotask(() => handleSelectReply(linkedThreadRow))
  }

  const selectedIndex = replies.findIndex(
    (r) => r.thread_id === selectedThreadId
  )

  const handlePrevReply = useCallback(() => {
    if (selectedIndex > 0) {
      handleSelectReply(replies[selectedIndex - 1])
    }
  }, [selectedIndex, replies, handleSelectReply])

  const handleNextReply = useCallback(() => {
    if (selectedIndex < replies.length - 1) {
      handleSelectReply(replies[selectedIndex + 1])
    }
  }, [selectedIndex, replies, handleSelectReply])

  const hasActiveFilters =
    !!debouncedSearch ||
    senderFilter.length > 0 ||
    campaignFilter.length > 0 ||
    readFilter.length > 0 ||
    classificationFilter !== 'all'

  // Only show full-page spinner on initial load, not on filter changes
  const isInitialLoad = isLoading && !repliesData
  const isWaitingForSequence = !!campaignId && isLoadingSequence

  if (isWaitingForSequence || isInitialLoad) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="size-6 animate-spin rounded-full border-2 border-brand border-t-transparent" />
      </div>
    )
  }

  // Empty state only when no filters are active and there are truly no replies
  if (replies.length === 0 && !hasActiveFilters) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-3 rounded-full bg-neutral-100 p-4">
          <PiChatText className="size-8 text-neutral-500" />
        </div>
        <p className="text-base text-neutral-800">No replies yet</p>
        <p className="mt-1 text-sm text-neutral-500">
          Replies from your campaign contacts will appear here.
        </p>
      </div>
    )
  }

  return (
    <div
      ref={captureSheetPortalEl}
      className={cn('relative flex flex-col gap-4 flex-1 min-h-0', className)}
    >
      <div className="flex flex-wrap items-center gap-3 shrink-0">
        <Ds2026SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search all replies..."
          aria-label="Search all replies"
          size="sm"
          className="flex-1 min-w-[240px]"
        />
        <ClassificationPills
          selected={classificationFilter}
          onChange={setClassificationFilter}
          summary={summaryData}
        />
        <InlineSelectFilter
          title="Sender"
          options={senderEmailOptions}
          selected={senderFilter}
          onApply={setSenderFilter}
          searchPlaceholder="Search senders..."
          ds2026
          size="sm"
        />
        {!campaignId && (
          <InlineSelectFilter
            title="Campaign"
            options={campaignOptions}
            selected={campaignFilter}
            onApply={setCampaignFilter}
            searchPlaceholder="Search campaigns..."
            ds2026
            size="sm"
          />
        )}
        <InlineSelectFilter
          title="Read status"
          options={READ_STATUS_OPTIONS}
          selected={readFilter}
          onApply={setReadFilter}
          singleSelect
          hideSearch
          align="end"
          ds2026
          size="sm"
        />
      </div>

      {linkedThreadId !== null && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-brand/20 bg-brand/5 px-4 py-2.5 text-sm">
          <PiEnvelope className="size-4 shrink-0 text-brand" />
          <span className="text-neutral-800">
            Showing thread from email link
          </span>
          <button
            type="button"
            onClick={clearLinkedThread}
            className="ml-auto inline-flex items-center gap-1 rounded-md border border-ds2026-btn-outline-stroke bg-ds2026-btn-outline-bg px-2.5 py-1 text-xs font-medium text-ds2026-btn-outline-text hover:bg-ds2026-btn-outline-hover-bg"
          >
            Show all replies
          </button>
        </div>
      )}

      <div className="flex flex-1 min-h-0 gap-0 overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
        <div
          ref={containerRef}
          className={cn(
            'w-96 shrink-0 overflow-y-auto border-r border-neutral-200 transition-opacity duration-200',
            isFetching && !isFetchingNextPage && replies.length > 0
              ? 'opacity-60'
              : 'opacity-100'
          )}
        >
          {replies.length === 0 && isFetching ? (
            <ReplyListSkeleton />
          ) : replies.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-sm text-neutral-500">
              No replies match your filters
            </div>
          ) : (
            <>
              {replies.map((reply) => (
                <ReplyListItem
                  key={reply.id}
                  reply={reply}
                  isSelected={selectedThreadId === reply.thread_id}
                  onClick={() => handleSelectReply(reply)}
                />
              ))}
              <div ref={sentinelRef} style={{ height: '1px' }} />
              {isFetchingNextPage && (
                <div className="flex items-center justify-center py-3">
                  <div className="size-4 animate-spin rounded-full border-2 border-brand border-t-transparent" />
                </div>
              )}
            </>
          )}
        </div>

        {createDealContext && createDealContext.company_type_slug && (
          // Server returns sub_category.slug (a free-form string); trusted here
          // because a bad slug would've failed earlier pipeline steps. Single
          // `CompanyTypeSlug` cast is reused for `tableTypeSlug` since
          // `TableTypeSlug` is a strict superset of `CompanyTypeSlug`.
          <Suspense fallback={null}>
            <CreateDealModal
              show
              handleClose={() => setCreateDealContext(null)}
              onDealCreated={() => {
                // Refresh the strip so the new "Linked to <deal>" badge
                // appears without a manual reload (FIR-7420).
                queryClient.invalidateQueries({
                  queryKey: [...replyThreadKeys.all, 'contact-company'],
                })
                setCreateDealContext(null)
              }}
              targetCompanyId={createDealContext.contact_company_id}
              totalSelectedRows={1}
              optimisticUiTarget="none"
              initialAccountOwnerId={user?.id}
              originCampaignId={createDealContext.campaign_id ?? undefined}
              initialSelectedProductId={
                createDealContext.product_id ?? undefined
              }
              sourceReplyThreadId={selectedReply?.thread_id ?? undefined}
              companyTypeSlug={
                createDealContext.company_type_slug as CompanyTypeSlug
              }
              tableTypeSlug={
                createDealContext.company_type_slug as CompanyTypeSlug
              }
            />
          </Suspense>
        )}

        {featureFlag.enableSamples && sampleRequestContext && (
          <Suspense fallback={null}>
            <SampleRequestFormModal
              show
              onHide={() => setSampleRequestContext(null)}
              initialCompanyId={sampleRequestContext.contactCompanyId}
              initialContact={sampleRequestContext.contact}
            />
          </Suspense>
        )}

        <div className="flex flex-1 flex-col min-h-0 min-w-0 overflow-hidden">
          {selectedReply ? (
            <>
              <div className="shrink-0">
                <ReplyDetailHeader
                  reply={selectedReply}
                  currentIndex={selectedIndex}
                  totalCount={replies.length}
                  onPrev={handlePrevReply}
                  onNext={handleNextReply}
                />
                <ReplyContactCompanyStrip
                  reply={selectedReply}
                  onCreateDeal={handleCreateDeal}
                  onRequestSample={handleRequestSample}
                  samplesEnabled={featureFlag.enableSamples}
                />
              </div>
              {/* Single scroll surface wrapping the thread *and* the
                  composer (FIR-7419) so the user can scroll back to the
                  original message while writing a reply. Auto-scrolls
                  the composer into view on open via its ref callback. */}
              <div className="flex-1 min-h-0 overflow-y-auto">
                {threadReplies.length > 1 ? (
                  <ThreadMessageList
                    replies={threadReplies}
                    actions={replyActions}
                  />
                ) : (
                  singleReplyData && (
                    <ThreadMessageCard
                      message={singleReplyData}
                      isLast
                      menuItems={singleReplyMenu}
                      senderBadge={senderBadge}
                    />
                  )
                )}
                {composeMode && selectedReply.taken_over_thread_id == null && (
                  <ReplyComposePanel
                    // Composite key remounts the panel when mode or sender
                    // changes so `useState(() => ...)` initializers re-run.
                    // Template inserts do NOT bump the key — they go through the
                    // imperative ref so recipients aren't wiped.
                    key={`${composeMode}-${composeIntegrationId ?? 'alias'}`}
                    ref={replyComposeRef}
                    reply={selectedReply}
                    mode={composeMode}
                    integrationId={composeIntegrationId}
                    scrollIntoViewRef={scrollComposerIntoView}
                    onDiscard={closeCompose}
                    onSent={closeCompose}
                    onModeChange={(nextMode) => openCompose(nextMode, null)}
                    onBrowseTemplates={() => setReplyTemplatesOpen(true)}
                    onSaveAsTemplate={handleSaveReplyTemplate}
                  />
                )}
              </div>
              <ReplyClassificationRow
                reply={classificationReply ?? selectedReply}
                onToggleInterested={() => {
                  const target = classificationReply ?? selectedReply
                  markInterested.mutate({
                    replyId: target.id,
                    isInterested: nextClassificationState(target.is_interested),
                  })
                }}
                onToggleFlag={() => {
                  if (selectedReply.thread_id) {
                    toggleFlag.mutate(selectedReply.thread_id)
                  }
                }}
              />
              {selectedReply.taken_over_thread_id != null ? (
                <TakenOverPill reply={selectedReply} />
              ) : composeMode ? null : (
                <ReplyActionBar
                  reply={selectedReply}
                  onCompose={openCompose}
                  onTakeOver={() => setTakeOverTargetReplyId(selectedReply.id)}
                />
              )}
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center text-sm text-neutral-500">
              Select a reply to view details
            </div>
          )}
        </div>
      </div>
      {selectedReply && (
        <TakeOverModal
          reply={selectedReply}
          show={takeOverTargetReplyId === selectedReply.id}
          onClose={() => setTakeOverTargetReplyId(null)}
          onConfirm={(integrationId) => {
            setTakeOverTargetReplyId(null)
            openCompose('reply', integrationId)
          }}
        />
      )}
      {selectedReply && selectedReply.contact_id != null && (
        <MismatchEmailModal
          reply={selectedReply}
          contactId={selectedReply.contact_id}
          show={mismatchTargetReplyId === selectedReply.id}
          onClose={() => setMismatchTargetReplyId(null)}
        />
      )}
      {/* Reply-templates sheet (FIR-7582): portaled into the FBPage container so
          its content-scoped overlay dims the entire page content (header, tabs,
          list, detail) — not just this tab — while leaving the app nav
          reachable, mirroring the sequence-template sheet's scope. */}
      {sheetPortalEl &&
        createPortal(
          <ReplyTemplatesDrawer
            open={replyTemplatesOpen}
            onClose={() => setReplyTemplatesOpen(false)}
            onUse={handleUseReplyTemplate}
            onAddClick={() => setSaveTemplateState({ open: true, body: '' })}
          />,
          sheetPortalEl
        )}
      {saveTemplateState.open && (
        <SaveReplyTemplateModal
          initialBody={saveTemplateState.body}
          onClose={() => setSaveTemplateState({ open: false, body: '' })}
        />
      )}
    </div>
  )
}
